import {
  CANCELLATION_POLICY_TEXT_MAX_LENGTH,
  CANCELLATION_POLICY_WINDOW_UNITS,
  CancellationPolicyWindowUnit,
} from '@shared/core/cancellation-policy'
import classNames from 'classnames'
import { ChangeEvent } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import {
  CharacterCounter,
  FormControl,
  SelectField,
  TextField,
  ToggleSwitch,
  Typography,
} from 'src/modules/core/components'
import type {
  CancellationPolicyFieldsProps,
  PolicyTextLayoutProps,
  PolicyToggleRowProps,
} from './cancellation-policy-fields.interface'
import classes from './cancellation-policy-fields.module.scss'

const UNIT_OPTIONS = CANCELLATION_POLICY_WINDOW_UNITS.map((unit) => ({ value: unit, label: unit }))

const LATE_CANCEL_HELP_ON =
  'Clients who cancel inside the window see a warning first and can still cancel. We record it as a late cancel for you to follow up.'
const LATE_CANCEL_HELP_OFF = "Off: clients can't cancel inside the window. They're asked to contact you."
const RESCHEDULE_HELP_OFF = "Off: clients can't reschedule online inside the window."
const POLICY_TEXT_HELP =
  'Clients see this on your booking page, confirmation page and emails, and when they try to cancel or reschedule inside the window.'

function PolicyToggleRow({ label, checked, onChange, stateLabel, help, disabled, testId }: PolicyToggleRowProps) {
  return (
    <FormControl label={label} labelClassName={classes.label}>
      <div className={classes.row}>
        <ToggleSwitch
          checked={checked}
          disabled={disabled}
          aria-label={label}
          data-testid={testId}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
        />
        <Typography variant="body" className={classes.stateLabel}>
          {stateLabel}
        </Typography>
      </div>
      <Typography variant="body" className={classes.help}>
        {help}
      </Typography>
    </FormControl>
  )
}

function PolicyToggleCard({ label, checked, onChange, help, disabled, testId }: PolicyToggleRowProps) {
  return (
    <div className={classNames(classes.toggleCard, { [classes.toggleCardDisabled]: disabled })}>
      <div className={classes.toggleCardText}>
        <Typography variant="body" className={classes.toggleCardTitle}>
          {label}
        </Typography>
        <Typography variant="body" className={classes.toggleCardHelp}>
          {help}
        </Typography>
      </div>
      <ToggleSwitch
        checked={checked}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        labelClassName={classes.toggleCardSwitch}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      />
    </div>
  )
}

function NoticeWindowField({ control, errors, onNoticeChange, fieldRefs }: CancellationPolicyFieldsProps) {
  const unit = useWatch({ control, name: 'notice_unit' })
  const value = useWatch({ control, name: 'notice_value' })
  return (
    <FormControl label="Notice window" labelClassName={classes.label} error={errors.notice_value}>
      <div className={classes.noticeRow}>
        <Controller
          name="notice_value"
          control={control}
          render={({ field }) => (
            <TextField
              ref={fieldRefs?.notice_value}
              type="number"
              inputMode="numeric"
              aria-label="Notice window"
              value={field.value}
              className={classNames(classes.numberInput, { [classes.inputError]: !!errors.notice_value })}
              wrapperClassName={classes.numberWrapper}
              onChange={(e) => {
                field.onChange(e.target.value)
                onNoticeChange(e.target.value, unit)
              }}
            />
          )}
        />
        <Controller
          name="notice_unit"
          control={control}
          render={({ field }) => (
            <SelectField
              variant="small"
              classNamePrefix="notice-unit"
              aria-label="Notice window unit"
              value={UNIT_OPTIONS.find((option) => option.value === field.value) || null}
              options={UNIT_OPTIONS}
              className={classes.unitSelect}
              onChange={(option: { value: CancellationPolicyWindowUnit } | null) => {
                const next = option?.value ?? 'hours'
                field.onChange(next)
                onNoticeChange(value, next)
              }}
            />
          )}
        />
        <Typography variant="body" className={classes.suffix}>
          before the appointment starts
        </Typography>
      </div>
    </FormControl>
  )
}

function PolicyTextField(props: CancellationPolicyFieldsProps) {
  const Layout = props.variant === 'card' ? CardPolicyText : DefaultPolicyText
  return (
    <Controller
      name="policy_text"
      control={props.control}
      render={({ field }) => (
        <Layout
          text={field.value}
          errors={props.errors}
          onTextChange={props.onTextChange}
          fieldRefs={props.fieldRefs}
        />
      )}
    />
  )
}

function DefaultPolicyText(props: PolicyTextLayoutProps) {
  return (
    <FormControl label="Policy text" labelClassName={classes.label} error={props.errors.policy_text}>
      <PolicyTextArea {...props} />
      <div className={classes.textFooter}>
        <Typography variant="body" className={classes.help}>
          {POLICY_TEXT_HELP}
        </Typography>
        <CharacterCounter count={props.text.length} max={CANCELLATION_POLICY_TEXT_MAX_LENGTH} />
      </div>
    </FormControl>
  )
}

function CardPolicyText(props: PolicyTextLayoutProps) {
  const hasError = !!props.errors.policy_text
  return (
    <FormControl error={props.errors.policy_text}>
      <div className={classes.textHeader}>
        <Typography
          variant="body"
          className={classNames(classes.label, classes.textLabel, { [classes.textLabelError]: hasError })}
        >
          Policy text
        </Typography>
        <CharacterCounter count={props.text.length} max={CANCELLATION_POLICY_TEXT_MAX_LENGTH} />
      </div>
      <PolicyTextArea {...props} />
      <Typography variant="body" className={classes.help}>
        {POLICY_TEXT_HELP}
      </Typography>
    </FormControl>
  )
}

function PolicyTextArea({ text, errors, onTextChange, fieldRefs }: PolicyTextLayoutProps) {
  return (
    <TextField
      ref={fieldRefs?.policy_text}
      Element="textarea"
      rows={3}
      aria-label="Policy text"
      value={text}
      className={classNames(classes.textarea, { [classes.inputError]: !!errors.policy_text })}
      onChange={(e) => onTextChange(e.target.value)}
    />
  )
}

function LateCancelToggleField({ control, variant }: CancellationPolicyFieldsProps) {
  const ToggleRow = variant === 'card' ? PolicyToggleCard : PolicyToggleRow
  return (
    <Controller
      name="allow_late_cancel"
      control={control}
      render={({ field }) => (
        <ToggleRow
          label="Allow late cancels"
          checked={field.value}
          onChange={field.onChange}
          stateLabel={field.value ? 'Allowed' : 'Blocked'}
          help={field.value ? LATE_CANCEL_HELP_ON : LATE_CANCEL_HELP_OFF}
          testId="cancellation-policy-allow-late-cancel"
        />
      )}
    />
  )
}

function RescheduleToggleField(props: CancellationPolicyFieldsProps) {
  const { control, rescheduleNotApplicableNote: notApplicableNote, rescheduleSettingLabel } = props
  const ToggleRow = props.variant === 'card' ? PolicyToggleCard : PolicyToggleRow
  const helpOn =
    props.rescheduleHelpOn ??
    `Clients can move the appointment inside the window. Requires "${rescheduleSettingLabel}" above.`
  return (
    <Controller
      name="allow_reschedule_in_window"
      control={control}
      render={({ field }) => (
        <ToggleRow
          label="Allow reschedules inside the window"
          checked={notApplicableNote ? false : field.value}
          onChange={field.onChange}
          disabled={!!notApplicableNote}
          stateLabel={notApplicableNote ? 'n/a' : field.value ? 'Allowed' : 'Blocked'}
          help={notApplicableNote ?? (field.value ? helpOn : RESCHEDULE_HELP_OFF)}
          testId="cancellation-policy-allow-reschedule"
        />
      )}
    />
  )
}

export function CancellationPolicyFields(props: CancellationPolicyFieldsProps) {
  return (
    <div className={classNames(classes.fields, { [classes.cardVariant]: props.variant === 'card' })}>
      <NoticeWindowField {...props} />
      <LateCancelToggleField {...props} />
      <RescheduleToggleField {...props} />
      <PolicyTextField {...props} />
    </div>
  )
}
